import { Controller } from "web-ui/controller"
import Sortable from "sortablejs"
import { getCSRFToken } from "web-ui/csrf"
import * as Log from "web-ui/log"

const dragCursorClass = "sortable-drag-active"

// Connects to data-controller="sortable"
export default class extends Controller {
  static values = {
    url: String,
    parameterName: String
  }

  connect() {
    this.debug("Connect")

    this.saving = false
    this.pendingOrder = null
    this.abortController = null

    this.sortable = Sortable.create(this.element, {
      animation: 150,
      forceFallback: true,
      fallbackOnBody: false,
      draggable: "[data-id]",
      handle: "[data-sortable-handle]",
      ghostClass: "sortable-placeholder",
      chosenClass: "sortable-chosen",
      dragClass: "sortable-dragging",
      onStart: this.startDrag,
      onEnd: this.endDrag,
      onUpdate: this.queueSave
    })

    this.confirmedOrder = this.sortable.toArray()
  }

  disconnect() {
    this.debug("Disconnect")

    this.sortable.destroy()

    document.body.classList.remove(dragCursorClass)

    if (this.abortController != null) {
      this.abortController.abort()
    }
  }

  startDrag = (event) => {
    this.sizeDraggingRow(event)

    document.body.classList.add(dragCursorClass)
  }

  endDrag = () => {
    document.body.classList.remove(dragCursorClass)
  }

  //## The dragging row is positioned rather than laid out with the table, so it is
  //## measured as a table of its own and its cells collapse to the width of their
  //## content. The dragged row's own column widths are copied onto it so that it
  //## remains aligned with the columns it was lifted out of.
  //## - Mac, Pedro, Thu Jul 30 2026
  sizeDraggingRow = (event) => {
    const draggingRow = Sortable.ghost

    if (draggingRow == null) {
      return
    }

    Array.from(event.item.children).forEach((cell, index) => {
      const width = cell.getBoundingClientRect().width

      draggingRow.children[index].style.width = width + "px"
    })
  }

  queueSave = () => {
    this.pendingOrder = this.sortable.toArray()

    this.debug("Queue Save", { order: this.pendingOrder })

    if (this.saving) {
      return
    }

    this.savePendingOrder()
  }

  async savePendingOrder() {
    this.saving = true
    this.abortController = new AbortController()

    try {
      while (this.pendingOrder != null) {
        const order = this.pendingOrder
        this.pendingOrder = null

        const response = await this.save(order)

        if (response.ok) {
          this.confirmedOrder = order
        } else {
          this.restoreConfirmedOrder()

          await this.reportRejectedOrder(response)
        }
      }
    } catch (error) {
      if (error.name === "AbortError") {
        return
      }

      this.restoreConfirmedOrder()

      Sentry.captureException(error)
    } finally {
      this.abortController = null
      this.saving = false
    }
  }

  save(order) {
    this.debug("Save", { order })

    const parameterName = this.parameterNameValue + "[]"
    const body = new URLSearchParams(order.map((id) => [parameterName, id]))

    return fetch(this.urlValue, {
      method: "POST",
      headers: {
        "Accept": "text/vnd.turbo-stream.html",
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        "X-CSRF-Token": getCSRFToken()
      },
      body,
      signal: this.abortController.signal
    })
  }

  restoreConfirmedOrder() {
    this.pendingOrder = null

    this.sortable.sort(this.confirmedOrder, true)
  }

  async reportRejectedOrder(response) {
    const contentType = response.headers.get("Content-Type")

    if (contentType != null && contentType.includes("text/vnd.turbo-stream.html")) {
      const html = await response.text()

      Turbo.renderStreamMessage(html)
      return
    }

    const error = new Error("Sortable save failed with status " + response.status)

    Sentry.captureException(error)
  }

  debug(message, data) {
    Log.debug("Sortable - " + message, data)
  }
}
